'use client'

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FaChevronRight, FaCalendarAlt, FaTag, FaUserCircle, FaFacebookF, FaTwitter, FaLinkedinIn, FaShareAlt, FaArrowLeft } from 'react-icons/fa'
import { Link, useNavigate } from 'react-router-dom'

const smoothScroll = (id) => {
  const targetElement = document.getElementById(id)
  if (targetElement) {
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

const generateIdFromText = (text) => {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
}

const SocialShare = ({ title, url }) => (
  <motion.div
    className="mt-8 flex space-x-6 justify-center"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5, duration: 0.5 }}
  >
    <Link to={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors">
      <FaFacebookF size={24} />
    </Link>
    <Link to={`https://twitter.com/intent/tweet?text=${title}&url=${url}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-600 transition-colors">
      <FaTwitter size={24} />
    </Link>
    <Link to={`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors">
      <FaLinkedinIn size={24} />
    </Link>
    <Link to={`mailto:?subject=${title}&body=Check this out: ${url}`} className="text-gray-600 hover:text-gray-800 transition-colors">
      <FaShareAlt size={24} />
    </Link>
  </motion.div>
)

const TableOfContents = ({ toc }) => (
  <motion.nav
    className="mt-8 p-6 bg-gray-100 rounded-lg"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
  >
    <h3 className="text-xl font-bold flex items-center mb-4 text-gray-800">
      <FaChevronRight className="mr-2" /> Table of Contents
    </h3>
    <ul className="space-y-2">
      {toc.map((item) => (
        <li key={item.id} className="text-sm">
          <Link
            to={`#${item.id}`}
            onClick={(e) => {
              e.preventDefault()
              smoothScroll(item.id)
            }}
            className="text-blue-600 hover:text-blue-800 transition-colors hover:underline"
          >
            {item.text}
          </Link>
        </li>
      ))}
    </ul>
  </motion.nav>
)

function BlogPostCard({ title, description, content = [], image, author, authorImage, date, categories, url }) {
  const [toc, setToc] = useState([])
  const [renderedContent, setRenderedContent] = useState([])
  const navigate = useNavigate()

  useEffect(() => {
    const tocItems = content
      .filter(item => ['h2', 'h3', 'h4'].includes(item.style))
      .map(item => ({
        text: item.children.map(child => child.text).join(' '),
        id: generateIdFromText(item.children.map(child => child.text).join(' ')),
      }))
    setToc(tocItems)

    const updatedContent = content.map(renderContent)
    setRenderedContent(updatedContent)
  }, [content])

  const renderContent = (block, index) => {
    const textContent = block.children.map((child) => (
      <span key={child._key}>
        {child.marks && child.marks.includes('strong') ? <strong>{child.text}</strong> : child.text}
      </span>
    ))
    const id = generateIdFromText(textContent.map(child => child.props.children).join(' '))

    switch (block.style) {
      case 'h2':
        return <motion.h2 key={index} id={id} className="text-3xl font-bold my-6 text-gray-800" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>{textContent}</motion.h2>
      case 'h3':
        return <motion.h3 key={index} id={id} className="text-2xl font-bold my-5 text-gray-800" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>{textContent}</motion.h3>
      case 'h4':
        return <motion.h4 key={index} id={id} className="text-xl font-bold my-4 text-gray-800" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>{textContent}</motion.h4>
      case 'blockquote':
        return <motion.blockquote key={index} className="italic border-l-4 border-blue-500 pl-4 my-4 text-gray-600" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>{textContent}</motion.blockquote>
      case 'normal':
      default:
        return <motion.p key={index} className="my-4 text-gray-700 leading-relaxed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>{textContent}</motion.p>
    }
  }

  return (
    <motion.div
      className="bg-white rounded-lg overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="px-8 pb-8">
        <motion.button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft className="mr-2" /> Back to Home
        </motion.button>
        {image && (
          <motion.img
            src={image}
            alt={title}
            className="w-full h-96 object-cover rounded-lg mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          />
        )}
        <motion.h1
          className="text-4xl font-bold mb-4 text-gray-900"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {title}
        </motion.h1>
        <motion.p
          className="text-xl text-gray-600 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {description}
        </motion.p>
        <motion.div
          className="flex items-center mb-6 text-sm text-gray-500 space-x-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="flex items-center">
            <FaUserCircle className="mr-2" />
            <span>{author}</span>
          </div>
          <div className="flex items-center">
            <FaCalendarAlt className="mr-2" />
            <span>{new Date(date).toLocaleDateString()}</span>
          </div>
          {categories && (
            <div className="flex items-center">
              <FaTag className="mr-2" />
              <span>{categories}</span>
            </div>
          )}
        </motion.div>

        {/* <TableOfContents toc={toc} /> */}

        <div className="mt-8 space-y-6">
          {renderedContent}
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-4 text-gray-800">Share this post:</h3>
          <SocialShare title={title} url={url} />
        </div>
      </div>
    </motion.div>
  )
}

export default BlogPostCard